import {compact} from '@benchling/js-utils';
import {ModelSetOptions} from 'backbone';

import BenchlingConfig from 'BenchlingConfig';
import {BufferViewLayout} from 'buffers/BufferComps';
import {Sequence} from 'components/Sequence';
import {Enzyme} from 'molbio/enzymes/backbone/Enzymes';
import {EnzymeType} from 'molbio/enzymes/EnzymeConstants';
import {MiniMapState} from 'molbio/maps/MapStates';
import SinglePolymerMapTabState, {
  SinglePolymerMapTabStateAttributes,
} from 'molbio/tabstates/SinglePolymerMapTabState';
import {TabStateDefaults} from 'Navigation';
import {
  constructOptions,
  MakeLocalSyncOptions,
  VisibilityOptions,
} from 'settings/visibility-options/VisibilityOptions';
import * as vo from 'settings/visibility-options/VisibilityOptions';

export interface CutsiteFilter {
  before: EnzymeType;
  after: EnzymeType | null;
  blackList: Array<Enzyme>;
}

export interface MiniMapTabStateAttributes extends SinglePolymerMapTabStateAttributes<
  Sequence,
  MiniMapState
> {
  snapshot: boolean;
  bufferViewLayout: BufferViewLayout | null;
  visibilityOptions: VisibilityOptions;
  cutsiteFilter: CutsiteFilter | null;
}

interface MiniMapTabState {
  get<K extends keyof MiniMapTabStateAttributes>(key: K): MiniMapTabStateAttributes[K];

  set<K extends keyof MiniMapTabStateAttributes>(
    attributeName: K,
    value: MiniMapTabStateAttributes[K],
    options?: ModelSetOptions
  ): MiniMapTabState;

  set(obj: Partial<MiniMapTabStateAttributes>): MiniMapTabState;
}

class MiniMapTabState extends SinglePolymerMapTabState<Sequence, MiniMapState> {
  mapState(options: {polymer: Sequence; visibilityOptions: VisibilityOptions}): MiniMapState {
    return new MiniMapState(options);
  }

  getVisibilityOptions(): VisibilityOptions {
    return this.get('visibilityOptions');
  }

  msVisibilityOptions(options?: MakeLocalSyncOptions): VisibilityOptions {
    const transcriptionLinksPresent = this.sequence()._sequence().tagSchemaHasTranscriptionLinkFields();
    const visibilityOptions = this.get('visibilityOptions');
    if (!transcriptionLinksPresent) {
      visibilityOptions.remove(visibilityOptions.where({name: vo.TRANSCRIPTIONS.name}));
    }
    options = {};

    if (this.get('snapshot')) {
      // Don't sync with the tabstate's VisibilityOptions,
      // ...which get pref sync'd.
      options.globalSync = false;
    }

    const msVo = visibilityOptions.makeLocalSync(options);

    if (this.get('snapshot')) {
      // Explicititly set which options are visible.
      const visible = compact([
        vo.MAXIMISE,
        vo.ANNOTATIONS,
        vo.TRANSLATIONS,
        BenchlingConfig.PARTS_ENABLE_OVERLAPPING_PARTS ? vo.TRANSCRIPTIONS : null,
        vo.PRIMERS,
        vo.SEQUENCE_AXIS,
      ]);
      const notVisible = [
        this.sequence().isRNASequence() ? vo.RNA_ORFS : vo.ORFS,
        vo.HOMOLOGY_REGION_AXIS,
        vo.CUTSITES,
        vo.DNA,
        vo.AMINO_ACIDS,
        vo.OVERHANGS,
        vo.COMPLEMENT,
        vo.INLINE_LABELS,
      ];
      for (const option of visible) {
        msVo.getOption(option.name)!.set('visible', true);
      }
      for (const option of notVisible) {
        msVo.getOption(option.name)!.set('visible', false);
      }
    }

    return msVo;
  }

  override defaults(): TabStateDefaults & Partial<MiniMapTabStateAttributes> {
    return {
      ...super.defaults(),
      title: 'Linear Map',
      snapshot: false,
      closeable: false,
      cutsiteFilter: null,
      bufferViewLayout: null,
      visibilityOptions: constructOptions('minimap', [
        {base: vo.MAXIMISE, visible: true, editable: false, sync: false},
        {base: vo.ANNOTATIONS, visible: true, editable: true, sync: true},
        {base: vo.TRANSLATIONS, visible: true, editable: true, sync: true},
        ...(BenchlingConfig.PARTS_ENABLE_OVERLAPPING_PARTS
          ? [
              {
                base: vo.PART_ANNOTATIONS,
                visible: true,
                editable: true,
                sync: true,
              },
            ]
          : []),
        {base: vo.TRANSCRIPTIONS, visible: true, editable: true, sync: true},
        {base: vo.PRIMERS, visible: true, editable: true, sync: true},
        {base: vo.CUTSITES, visible: true, editable: true, sync: true},
        {base: vo.ORFS, visible: false, editable: true, sync: true},
        {base: vo.SEQUENCE_AXIS, visible: true, editable: true, sync: true},
        {base: vo.HOMOLOGY_REGION_AXIS, visible: false, editable: false, sync: false},
        {base: vo.DNA, visible: false, editable: false, sync: true},
        {base: vo.COMPLEMENT, visible: false, editable: false, sync: true},
        {base: vo.OVERHANGS, visible: false, editable: false, sync: true},
        {base: vo.AMINO_ACIDS, visible: false, editable: false, sync: true},
        {base: vo.INLINE_LABELS, visible: false, editable: true, sync: true},
        {base: vo.PARTS, visible: true, editable: false},
      ]),
      preferenceKey: 'mini-map',
    };
  }
}

export default MiniMapTabState;
